import api from '../utils/api';

const BASE = '/api/classrooms';

/**
 * Fetch a classroom, fully populated (students, teachers, owner, scenarios, folders, announcements).
 * @route GET /api/classrooms/classroom/:classroomId
 * @param {string} classroomId
 * @returns {Promise<Object>} The populated classroom
 */
export const getClassroom = (classroomId) =>
    api.get(`${BASE}/classroom/${classroomId}`).then((r) => r.data);

/**
 * List classrooms owned by a user.
 * @route GET /api/classrooms/user/:userId
 * @param {string} userId
 * @returns {Promise<Object[]>} Owned classrooms
 */
export const getClassroomsByUser = (userId) =>
    api.get(`${BASE}/user/${userId}`).then((r) => r.data);

/**
 * Create a classroom owned by the authenticated user.
 * @route POST /api/classrooms
 * @param {FormData|Object} data - name and optional image (FormData for upload)
 * @returns {Promise<Object>} The created classroom
 */
export const createClassroom = (data) =>
    api.post(BASE, data).then((r) => r.data);

/**
 * Update a classroom. Accepts FormData for an image change, or a plain object.
 * @route PATCH /api/classrooms/:classroomId
 * @param {string} classroomId
 * @param {FormData|Object} data
 * @returns {Promise<Object>} The updated, populated classroom
 */
export const updateClassroom = async (classroomId, data) => {
    const headers = data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {};
    const r = await api.patch(`${BASE}/${classroomId}`, data, { headers });
    return r.data;
};

/**
 * Delete a classroom (and unlink it from members; recursively deletes its folders).
 * @route DELETE /api/classrooms/:classroomId
 * @param {string} classroomId
 * @returns {Promise<Object>} The deleted classroom
 */
export const deleteClassroom = (classroomId) =>
    api.delete(`${BASE}/${classroomId}`).then((r) => r.data);

/**
 * Regenerate a classroom's join code.
 * @route PATCH /api/classrooms/:classroomId/regenerateClassroomCode
 * @param {string} classroomId
 * @returns {Promise<{message: string, classroomCode: string}>}
 */
export const regenerateClassroomCode = (classroomId) =>
    api.patch(`${BASE}/${classroomId}/regenerateClassroomCode`).then((r) => r.data);

/**
 * Generate a signed (7-day) invite link for a classroom and role.
 * @route POST /api/classrooms/generateInviteLink
 * @param {string} classroomId
 * @param {string} role - 'student' or 'teacher'
 * @returns {Promise<{inviteLink: string}>}
 */
export const generateInviteLink = (classroomId, role) =>
    api.post(`${BASE}/generateInviteLink`, { classroomId, role }).then((r) => r.data);

/**
 * Search students within a classroom (fuzzy username/email).
 * @route POST /api/classrooms/searchClassroomUsers/:classroomId
 * @param {string} classroomId
 * @param {string} searchTerm
 * @returns {Promise<{info: Object[]}>} Matching students under `info`
 */
export const searchClassroomUsers = (classroomId, searchTerm) =>
    api.post(`${BASE}/searchClassroomUsers/${classroomId}`, { searchTerm }).then((r) => r.data);

/**
 * Remove a user from a classroom by role.
 *
 * The classroom leaves their joinedClassrooms only once no role remains, and losing the
 * student role also detaches them from the classroom's assignments.
 * @route PATCH /api/classrooms/removeUserFromClassroom/:classroomId
 * @param {string} classroomId
 * @param {string} userId
 * @param {string} role - 'student' or 'teacher'
 * @returns {Promise<string>} Confirmation message
 */
export const removeUserFromClassroom = (classroomId, userId, role) =>
    api.patch(`${BASE}/removeUserFromClassroom/${classroomId}`, { userId, role }).then((r) => r.data);

/**
 * Change a user's role within a classroom (moves them between students/teachers).
 *
 * Promotion detaches them from the classroom's assignments; demotion reattaches them to the
 * assignToAll ones, so an accidental promotion can be undone.
 * @route PATCH /api/classrooms/changeUserRole/:classroomId
 * @param {string} classroomId
 * @param {string} userId
 * @param {string} role - target role: 'student' or 'teacher'
 * @returns {Promise<string>} Confirmation message
 */
export const changeUserRole = (classroomId, userId, role) =>
    api.patch(`${BASE}/changeUserRole/${classroomId}`, { userId, role }).then((r) => r.data);

/**
 * Get a student's logs grouped by assignment id (choice decisions annotated with text).
 * @route GET /api/classrooms/getLogsStudent/:classroomId/:studentId
 * @param {string} classroomId
 * @param {string} userId - the student's id
 * @returns {Promise<Object>} Logs keyed by assignment id
 */
export const getLogsStudent = (classroomId, userId) =>
    api.get(`${BASE}/getLogsStudent/${classroomId}/${userId}`).then((r) => r.data);

/**
 * Get a classroom's assignments, each with their logs embedded.
 * @route GET /api/classrooms/getAssignmentsWithLogs/:classroomId
 * @param {string} classroomId
 * @returns {Promise<Object[]>} Assignments with embedded logs
 */
export const getAssignmentsWithLogs = (classroomId) =>
    api.get(`${BASE}/getAssignmentsWithLogs/${classroomId}`).then((r) => r.data);
